<template>
    <base-layout>
        <ul class="nav navbar-nav navbar-left" slot="toolbar_buttons">
            <li>
                <date-range-picker :dateFields="dateFields" :datesRange="datesRange" @dates-range-updated="updatedDates"></date-range-picker>
            </li>

            <li v-if="isAdmin">
                <button type="button" class="btn btn-icon" v-on:click="refreshDashboard" v-tooltip.bottom="'Refresh Dashboard'">
                    <i class="fa fa-lg fa-refresh"></i>
                </button>
            </li>

            <li>
                <button class="btn btn-icon" v-bind:class="{filtered: showLeadsTable }"
                        v-on:click="toggleLeadsTable">
                    <i class="fa fa-table"></i>
                </button>
            </li>

            <li v-if="isAdmin">
                <button type="button" class="btn btn-icon" v-on:click="showComponentModal()" v-tooltip.bottom="'Add Component'">
                    <i class="fa fa-lg fa-plus"></i>
                </button>
            </li>

            <li v-if="isAdmin">
                <button type="button" class="btn btn-icon" v-on:click="isDashboardModalVisible = true" v-tooltip.bottom="'Edit Dashboard'">
                    <i class="fa fa-lg fa-pencil"></i>
                </button>
            </li>

        </ul>

        <!-- slot main -->

        <div id="dashboard-canvas" class="col-xs-12 main with-leads-table" slot="main" v-bind:class="{ 'with-leads-table-active': showLeadsTable }">
            <!-- components -->
            <div class="row ls-charts">
                <div class="col-xs-12 spinner-container" v-if="isLoading">
                    <i class="fa fa-circle-o-notch fa-spin fa-3x fa-fw" aria-hidden="true"></i>
                </div>
                <div class="pivots-wrapper" v-if="currentPivots.length">
                    <span v-for="pivot, pivotIx in currentPivots" class="pivot label">
                        <template v-if="pivot">
                            {{ pivot.field.name }}:
                            <label><input v-model="pivot.is_not" type="checkbox" @change="updatePivotValue(pivot, pivotIx)"> is not</label>&nbsp;
                            <span v-if="pivot.isLoading"><i class="fa fa-circle-o-notch fa-spin"></i>Loading ...</span>
                            <select v-else v-model="pivot.value" @change="updatePivotValue(pivot, pivotIx)">
                                <option :disabled="!pivot.options" :value="null">
                                    <span><i>- none -</i></span>
                                </option>
                                <option v-for="v in pivot.options" :value="v">{{ v }}</option>
                            </select>
                        </template>
                    </span>
                </div>

                <h1 v-if="dashboard.name">{{ dashboard.name }}</h1>

                <div class="col-xs-12 ls-charts-wrapper">
                    <div class="grid-container">
                        <!-- v-if="dashboard.active_components_count" -->
                        <div class="grid" v-if="components.length">
                            <div class="col-xs-12">
		                <grid-layout
                                    :layout="components"
                                    :col-num="6"
                                    :row-height="60"
                                    :is-draggable="isAdmin"
                                    :is-resizable="isAdmin"
                                    :vertical-compact="true"
                                    :margin="[10, 10]"
                                    :use-css-transforms="false"
                                    :responsive="false"
                                    :auto-size="true">

                                    <!-- v-if="!item.draft" -->
                                    <grid-item v-for="(item, index) in components"
                                               ref="components"
                                               :key="item.i"
                                               :x="item.x"
                                               :y="item.y"
                                               :w="item.w"
                                               :h="item.h"
                                               :i="item.i"
                                               :minW="minW(item)"
                                               :maxW="maxW(item)"
                                               :minH="minH(item)"
                                               :maxH="maxH(item)"
                                               :drag-allow-from="'.panel-header h3'"
                                               @moved="layoutUpdated"
                                               @resized="layoutUpdated">
                                        <component-box v-on:deleteComponent="deleteComponent"
                                                       v-on:cloneComponent="cloneComponentInDashboard"
                                                       v-on:showEditModal="showComponentModal"
                                                       :width="item.w"
                                                       :height="item.h"
                                                       :component="item"
                                                       :dashboardRules="computedFilters"
                                                       :index="index"
                                                       :datesRange="datesRange">

                                        </component-box>
                                    </grid-item>
		                </grid-layout>
                            </div>
                        </div>
                        <div class="col-xs-offset-3 col-xs-6" v-else>
                            <div class="well well-lg empty-msg">
		                This dashboard is empty, start by <a class="text-primary" v-on:click="showComponentModal()">adding</a> the first chart.
                            </div>
                        </div>
                    </div>
	        </div>
            </div>
            <div v-if="showLeadsTable" class="row ls-leads-table">
                <div class="col-xs-12 leads-queue-wrapper">
                    <div class="box panel leads-queue full-height">
                        <div class="panel-content chart-wrapper full-height">
                            <div class="endless-table-container full-height">
                                <endless-table
                                    :dashboard-id="dashboard.id"
                                    :isAdmin="isAdmin"
                                    v-bind:rules="computedFilters"
			            v-bind:datesRange="datesRange"
			            v-bind:pickedColumns="pickedColumns"
                                    v-bind:showLeadsTable="showLeadsTable"
                                    @showColumnPicker="openColumnPicker()"
                                    @leadsDownload="download()"
                                    v-on:do-text-search="doTextSearch">
		                </endless-table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <div slot="modals">
            <dashboard-modal v-if="isDashboardModalVisible && isAdmin"
                             v-bind:dashboardId="dashboardId"
                             v-on:save="fetchDashboard(true)"
                             v-on:clone="cloneDashboard"
                             v-on:close="isDashboardModalVisible = false">
            </dashboard-modal>

            <component-modal v-if="isModalVisible && isAdmin"
                             v-on:close="closeComponentModal"
                             v-on:showModal="showComponentModal"
                             v-bind:dashboardRules="computedFilters"
                             v-bind:component="editedComponent"
                             v-bind:datesRange="datesRange">
            </component-modal>

            <columns-picker-modal v-if="showColumnPicker && isAdmin"
                                  v-bind:available-columns="availableColumns"
                                  v-on:close="showColumnPicker = false"
                                  v-on:setColumns="setColumns">
            </columns-picker-modal>

        </div>
    </base-layout>
</template>

<script>
 /**
  * https://github.com/jbaysolutions/vue-grid-layout
  */
 import Vue from 'vue';

 import VueGridLayout from 'vue-grid-layout';

 var GridLayout = VueGridLayout.GridLayout
 var GridItem = VueGridLayout.GridItem

 import {BaseLayout} from 'components/layouts/';
 import {Modal, LeadsModal, DashboardModal} from 'components/modals/';

 import LsChart from 'components/elements/PlotlyJS/LSBasicChart';
 import ComponentBox from 'components/elements/ComponentBox';
 import ComponentModal from 'components/modals/ComponentModal';
 import DateRangePicker from 'components/elements/DateRangePicker';
 import EndlessTable from 'components/elements/EndlessTable';
 import ColumnsPickerModal from 'components/modals/ColumnsPickerModal';


 // Load the full build.
 var _ = require('lodash');

 export default {
     components: {
         BaseLayout,
         GridLayout,
         GridItem,
         LsChart,
         ComponentBox,
         ComponentModal,
         DashboardModal,
         DateRangePicker,
         EndlessTable,
         ColumnsPickerModal
     },
     data () {
         return {
             /* These are part of colums */
             isDirty: false,
             showLeadsTable: false,
             isAdmin: false,
             currentPivots: [],
             showColumnPicker: false,
             showPivotPicker: false,
             pivotValues: [],
             pivotValue: null,
             nextIndex: 0,
             query: {
	         q: null
             },
             availableColumns: [],
             dateFields: [],
             fieldsOptions: [
	         {
	             label: 'Meta',
	             fields: [
	                 { name: 'id', val: 'id' },
	                 { name: 'timestamp', val: 'timestamp' },
	                 { name: 'status', val: 'status' },
	                 { name: 'score', val: 'score' }
	             ],
	         },
             ],

             pagination: {
	         current_page: 1, // required
             },
             leads: [],
             placeholder: 'Columns',
             table: null,
             editName: false,
             isLoading: true,
             isDashboardModalVisible: false,
             isModalVisible: false,
             targetComponent: undefined,
             editedComponent: undefined,
             datesRange: {'from': null, 'to': null, 'date_field': null},
             name: "",
             versions: {},
             dashboard: {
	         id: null,
                 thing_type: {id: null, name: ''},
	         name: null,
	         components: [],
	         cols: [],
                 date_from: null,
                 date_to: null,
                 date_field: null,
                 pivots: [],
                 filter_rule: null
             },
             components: []
         };
     },
     computed: {
         pivot () {
             var operands = _.map(_.filter(this.currentPivots, (x) => x && x.value != null),
                                  (p) => {
                                      if (p.field.data_type == 'BooleanField') {
                                          return {
                                              expression_type: 'rule',
                                              field: p.field.id,
                                              operator: p.value ? 'is_true' : 'is_false',
                                              values: null,
                                              negate: p.is_not
                                          };
                                      }
                                      else {
                                          return {
                                              expression_type: 'rule',
                                              field: p.field.id,
                                              operator: 'is_any_of',
                                              values: [p.value],
                                              negate: p.is_not
                                          };
                                      }

             });

             if (operands.length) {
                 return {
                     expression_type: 'and',
                     negate: false,
                     operands: operands
                 }
             }
             return null;
         },
         computedFilters () {
             if (this.pivot && this.dashboard.filter_rule) {
                 return {
                     expression_type: 'and',
                     operands: [this.pivot, this.dashboard.filter_rule]
                 }
             }
             else if (this.pivot) {
                 return this.pivot;
             }
             else if (this.dashboard.filter_rule) {
                 return this.dashboard.filter_rule;
             }
             return null;
         },
         tableHeight() {
             return $(window).height() - 200;
         },
         chartHeight () {
             return $(window).height() - 350;
         },
         pickedColumns () {
             if (this.availableColumns.length === 0) {
	         return undefined;
             }
             return this.availableColumns.filter(function (e) { return e.picked; });
         },
         dashboardId () {
             return this.$route.params.id;
         }
     },
     watch: {
         $route () {
             if (this.$route.params.id) {
                 this.dashboard = {
	             id: null,
	             name: null,
	             type: '',
	             components: [],
	             cols: [],
                     date_from: null,
                     date_to: null,
                     date_field: null,
                     filter_rule: null
                 };

	         this.fetchDashboard(true);
             }
         },
         isDraggable() {
             //alert('drag');
         },
         /* pickedColumns: {
          *     // TODO: Revisit this
          *     handler: function (val, oldVal) {
          *         if (!this.isAdmin) {
          *             return;
          *         }
	    var fields = val.map(function (f) {
	    if (f.path) {
	    return f.path + "." + f.slug;
	    }
	    return f.slug;
	    });
          *         this.query.fields = fields;
          *         this.dashboard.cols = fields;

          *     },
          *     deep: true
          * } */
     },

   created () {
       var u = this.$store.state.user;
       this.isAdmin = u.role == "superuser";
       this.fetchDateFieldOptions();

       if (this.dashboardId) {
           this.fetchDashboard(true);
       }
       BUS.$on('refreshDashboard', this.refreshDashboard);
   },

   beforeMount() {
     var self = this;
     BUS.$on('canISwitchAccount',
             function (permission) {
               permission.ok = self.preventLeave();
               if (permission.ok) {
                 self.isDirty = false;
               };
             });

     window.addEventListener("beforeunload", this.preventLeave);
     this.$once("hook:beforeDestroy", () => {
       BUS.$off('canISwitchAccount');
       window.removeEventListener("beforeunload", this.preventLeave);
     })
     },
     // Handling route changes and unsaved work
     beforeRouteUpdate(to, from, next) {
         var ok = this.preventLeave();
         if (!ok) {
             return;
         }
         next();

     },
     beforeRouteLeave(to, from, next) {
         var ok = this.preventLeave();
         if (!ok) {
             return;
         }
         next();
     },
     methods: {
          async fetchDateFieldOptions() {
             var self = this;
             let dateFields = await API.dateFields.getList();
             self.dateFields = [{id: '', value: '', label: 'default', name: 'default'}]
                               .concat(dateFields.data.map( (x) => { return { id: x.id, value: x.id, label: x.name, name: x.name} }));
         },
         updatePivotValue(pivot, pivotIx) {
             this.$set(this.currentPivots, pivotIx, pivot);
         },
         doTextSearch(q) {
             this.query.q = q;
         },
         preventLeave(event) {
             var ok = true;
             if (this.isDirty) {
                 ok = window.confirm("This page is asking you to confirm that you want to leave - changes you have made may not be saved.");
                 if ((typeof event !== "undefined") && !ok) {
                     event.preventDefault();
                     event.returnValue = "";
                 }
             }
             return ok;
         },
         toggleEditName() {
             this.editName = !this.editName;
             this.name = this.dashboard.name;
             if (this.editName) {
                 this.$nextTick(() => {
                     this.$refs.nameEditor.focus();
                 });
             }
         },
         toggleLeadsTable() {
             this.showLeadsTable = !this.showLeadsTable;
         },
         connectDashboardSave (dashboard, redirecting) {
             // Updating Sidebar LiST
             BUS.$emit('refreshDashboardsList');
             if (redirecting) {
	         // redirecting onCreate
	         this.$router.push({ name: 'user-dashboard-detail', force: true, params: { id: dashboard.id } });
             } else {
	         this.fetchDashboard();
             }
             // closing modal
             this.isDModalVisible = false;
         },
         editDashboard() {
             this.editDashboardModal = true;
         },
         updatedDates: _.debounce(function (datesRange) {
             let changed = !_.isEqual(this.datesRange, datesRange);
             Vue.set(this, 'datesRange', datesRange);
         }),
         deleteComponent(key) {
             this.components = this.components.filter(item => item.i != key);
             this.saveDashboardComponents();
         },
         cloneDashboard() {
             var self = this;
             self.isDashboardModalVisible = false;
             var d = _.cloneDeep(this.dashboard);
             delete d.id;
             d.name = "Copy of " + d.name;

             if (d.thing_type != null) {
                 d.thing_type_id = d.thing_type.id;
             }

             d.components = d.components.map((c) => {
                 return self.cloneComponent(c);
             });
             self.saveDashboard(d);
         },
         refreshDashboard() {
             // This is an ugly hack
             var dr = _.cloneDeep(this.datesRange);
             Vue.set(this, 'datesRange', null);
             Vue.set(this, 'datesRange', dr);
         },
         fetchDashboard (change) {
             var is_change = typeof change !== "undefined" && change
             var scroll_top = is_change;
             var self = this;
             self.components = [];
             self.isLoading = true;
             self.currentPivots = [];
             return API.dashboards.read(self.dashboardId)
	               .then(function (response) {
	                   self.dashboard = response.data;

                           if (is_change) {
                               // We are fetching a new dashboard
                               // TODO: revisit this

                               Vue.set(self, 'datesRange', self.$store.getters.getDateRangeByField(self.dashboard.date_field));
                           }
                           else {
                               self.datesRange.date_field = self.dashboard.date_field;
                           }

	                   if (self.dashboard.cols) {
	                       self.query.fields = self.dashboard.cols;
	                   }

                           self.fetchFields();

                           self.components = self.dashboard.components.map((item, index) => {
                               let c = _.cloneDeep(item);
                               c.i = index.toString();
                               return c;
                           });

                           self.nextIndex = self.components.length;

                           if (scroll_top) {
                               var container = document.getElementById('main-container');
                               container.scrollTop = 0;
                           }

                           self.setPivots(response.data.pivots);
	                   self.isLoading = false;
                           self.isDirty = false;
	               });
         },
         download: _.debounce(function (params) {
             var queryRequest = _.cloneDeep(this.query);
             queryRequest.c = JSON.stringify(this.computedFilters);

             delete queryRequest['fields'];

             if (!queryRequest.q) {
                 // Move this
                 delete queryRequest.q;
             }

             if (this.datesRange.from) {
	         queryRequest.from = this.datesRange.from;
             }

             if (this.datesRange.to) {
	         queryRequest.to = this.datesRange.to;
             }

             if (this.datesRange.date_field) {
	         queryRequest.date_field = this.datesRange.date_field;
             }

             if (this.dashboard.id) {
                 queryRequest.dashboard_id = this.dashboard.id;
             }

             var self = this;

             API.leads.downloadLeads(queryRequest);

         }, 100),

         minW(component) {
             if (component.chart == "MB") {
                 let series = component.images.reduce((acc, curr) => acc + curr.series.length, 0);

                 return 1 * series;
             }
             return 3;
         },

         maxW(component) {
             if (component.chart == "MB") {
                 let series = component.images.reduce((acc, curr) => acc + curr.series.length, 0);
                 return 2 * series;
             }
             return 9;
         },

         minH(component) {
             switch (component.chart) {
                 case "MB":
                     let series = Math.ceil(component.images.reduce((acc, curr) => acc + curr.series.length, 0)/6);
                     return 2 * series;
                 case "TB":
                     return 3;
                 default:
                     return 6;
             }
         },

         maxH(component) {
             switch (component.chart) {
                 case "MB":
                     let series = Math.ceil(component.images.reduce((acc, curr) => acc + curr.series.length, 0)/6);
                     return 3 * series;
                 case "TB":
                     return 6;
                 default:
                     return 9;
             }
         },
         layoutUpdated: _.debounce(function () {
             this.isDirty = true;
             this.saveDashboardComponents();
             this.$emit('resized');
         }, 150),
         savePickedColumns() {
             if (!this.isAdmin) {
                 return;
             }

             var req = {
                 id: this.dashboard.id,
                 cols: _.cloneDeep(this.dashboard.cols)
             };
             this.saveDashboard(req);
         },
         saveDashboardComponents() {
             if (!this.isAdmin) {
                 return;
             }
             var req = {
                 id: this.dashboard.id,
                 components: _.cloneDeep(this.components)
             }

             this.saveDashboard(req);
         },
         saveDashboard: _.debounce(function (req) {
             var self = this;
             API.dashboards.save(req)
                .then(function(res) {
                    if (!req.id) {
                        self.connectDashboardSave(res.data, true);

                    }
                    else {
                        Vue.toast("Dashboard updated correctly", {className: ['et-info']});
                        self.isDirty = false;
                    }
                })
                .catch(() => Vue.toast("Unexepected error", {className: ['et-error']}));
         }, 500),
         showComponentModal (index) {
             if (!this.isAdmin) {
                 return;
             }

             // Setting render target model
             if (typeof index !== "undefined") {
                 this.targetComponent = index;
                 this.editedComponent = this.components[index];
             }
             else {
                 this.targetComponent = undefined;
                 this.editedComponent = undefined;
             }
             // Showing modal
             this.isModalVisible = true;
         },
         closeComponentModal (component) {
             if (!this.isAdmin) {
                 return;
             }

             if (component) {
                 this.isDirty = true;
                 let c = _.cloneDeep(component);
                 let self = this;
                 let componentRefIndex = this.targetComponent;
                 if (typeof this.targetComponent === "undefined") {
                     c.w = this.minW(c);
                     c.h = this.minH(c);
                     c.x = 0;
                     c.y = this.components.reduce(function (acc, current) {
                         let yCurr = current.y + current.h;
                         return (yCurr > acc) ? yCurr : acc;
                     }, 0);
                     c.i = this.nextIndex.toString();
                     this.nextIndex++;
                     this.components.push(c);
                     componentRefIndex = this.components.length - 1;
                 }
                 else {
                     this.$set(this.components, this.targetComponent, c);
                 }
                 this.$nextTick(() => self.$refs.components[componentRefIndex].$children[0].fetchRenderData());
                 this.layoutUpdated();
             }

             // Setting modal parameter


             this.targetComponent = undefined;
             this.editedComponent = undefined;
             // Hidding modal
             this.isModalVisible = false;
             // Refreshing dashboard view
             // if (component) {
             //   this.fetchDashboard();
             // }
         },
         cloneComponent(component) {
             var c = _.cloneDeep(component);
             delete c.id;
             delete c.dashboard;

             c.domains.forEach((d) => {
                 delete d.id;
                 delete d.component;
             });

             c.images.forEach((i) => {
                 delete i.id;
                 delete i.component;
                 i.series.forEach((s) => {
                     delete s.id;
                 });
             });
             return c;
         },
         cloneComponentInDashboard(component) {
             var clone = this.cloneComponent(component);
             clone.i = this.nextIndex.toString();
             this.nextIndex++;
             this.components.push(clone);
             this.layoutUpdated();
         },
         openColumnPicker() {
             this.showColumnPicker = true;
         },
         setColumns(columns) {
             this.isDirty = true;
             this.availableColumns = columns;
             this.showColumnPicker = false;
             var fields = this.pickedColumns.map(f => f.id);
             this.query.fields = fields;
             this.dashboard.cols = fields;
             this.savePickedColumns();
         },
         async setPivots(pivots) {
             var self = this;
             self.currentPivots = new Array(pivots.length);
             await _.map(pivots, async (field, index) => {
                 let item = {
                     field: field,
                     value: null,
                     is_not: false,
                     options: [],
                     isLoading: true
                 };
                 if (field.data_type == 'BooleanField') {
                     item.options = [true, false];
                 }
                 else {
                     let values = await API.fields.values(field.id);
                     item.options = values.body;
                 }
                 item.isLoading = false;
                 self.$set(self.currentPivots, index, item);
             });
         },
         showLeadsQueueModal (component) {
             if (!this.isAdmin) {
                 return;
             }
             // Setting render target model
             this.targetComponent = component;
             // Showing modal
             this.isLeadsQueueVisible = true;
         },
         closeLeadsQueueModal () {
             if (!this.isAdmin) {
                 return;
             }
             // Setting modal parameter
             this.targetComponent = undefined;
             // Hidding modal
             this.isLeadsQueueVisible = false;
         },
         async fetchFields (callback) {
             var self = this;
             var labelsChoices = {
	         M: { label: 'Meta', path: '', values: [] },
	         S: { label: 'Score', path: '', values: [] },
	         L: { label: 'Lead', path: 'fields', values: [] },
	         C: { label: 'CRM', path: 'crm_fields', values: [] },
                 P: { label: 'Phone Validation', path: 'appended.phone_validation', values: []},
                 O: { label: 'System', path: '', values: []}
             };

             var response = await API.fields.getList({'indexed': 'true'})
             var results = response.data;

             var refs = await API.fieldReferences.getAvailableChoices();

	     if (!self.query.fields) {
	         self.query.fields = [];
	     }

             /* self.availableColumns = results.map(c => {
              *     return {
              *         id: c.id,
              *         field_type: c.field_type,
              *         label: c.label,
              *         name: c.name,
              *         slug: c.slug,
              *         path: labelsChoices[c.field_type].path,
              *         picked: self.query.fields.indexOf(c.id) >= 0,
              *         data_type: c.data_type
              *     };
              * }); */

	     /* Defaults for availableColumns */
	     var columns = [];

	     for (var index in results) {
	         let field = results[index];

                 let t = field.field_type in labelsChoices ? field.field_type : 'O';

                 let f = {
                     id: field.id,
                     pk: field.id,
                     field_type: t,
                     type: t,
	             path: labelsChoices[t].path,
	             name: field.name,
	             slug: field.slug,
                     data_type: field.data_type,
	             picked: self.query.fields.indexOf(field.id) >= 0
                 };

                 if (f.pk == self.dashboard.pivot) {
                     self.pickedPivot = f;
                 }

	         columns.push(f);

	         var path = labelsChoices[t].path;
	         labelsChoices[t].values.push({
	             name: f.name,
	             val: (path != '' ? path + '.' + f.slug : f.slug),
	         })
	     }

             refs.data.forEach(function (item) {
                 if (item.kind == 'phone_validation') {
                     item.choices.forEach(function (f) {
                         columns.push(
                             {
                                 type: 'P',
                                 path: 'appended.phone_validation',
                                 name: f[0],
                                 slug: f[1],
                                 data_type: {id: 'TextField', name: "Text", type: "basic"},
                                 picked: self.query.fields.indexOf('appended.phone_validation.' + f[1]) >= 0
                             }
                         );
                     });
                 }
             });

	     // We need to sort the fields based on self.query.fields
	     var headColumns = [];
	     self.query.fields.forEach(function (item) {
	         var i = API.fields.findFieldByPath(columns, item);
	         if (i >= 0) {
	             headColumns.push(columns.splice(i, 1)[0]);
	         }
	     });
	     self.availableColumns = headColumns.concat(columns);

	     for (var index in labelsChoices) {
	         var choice = labelsChoices[index];
	         self.fieldsOptions.push({
	             label: choice.label, fields: choice.values
	         });
	     }

             if (typeof callback !== 'undefined') {
                 callback();
             }
 	     // Fetching initial rows
	         // self.fetchLeads();
         },
     }
 }
</script>

<style lang="scss">

 .empty-msg {
     margin: 30px auto;
 }


 /* Leads Queue styles */
 h1 .btn {
     background-color: transparent;
     margin-top: -8px;
 }

 h1 input {
     background: #fff;
     border: 0px!important;
     padding: 0px;
     padding-top: 4px;
     margin-top: -4px;
 }

 .leads-queue {
     padding: 0px!important;
     border: 1px solid #999;
     border-bottom: 0px;
     margin: 0px!important;
     float: none;
 }

 .leads-queue .chart-wrapper {
     padding: 0px!important;
     height: 100%!important;
 }

 .endeless-table-container {
     padding: 10px;
 }

 .leads-queue-wrapper {
     padding: 0px;
     position: absolute;
     height: 100%;
 }

 .leads-queue.full-height {
     position: absolute;
     height: 100%;
     width: 100%;
 }

 #dashboard-canvas {
     display: flex;
     flex-direction: column;
 }

 .ls-charts {
     display: flex;
     flex-direction: column;
     flex: 100vh;
     flex-grow: 1;
     width: 100%;
     overflow: hidden;
     position: relative;
     margin: 0px!important;
 }

 .ls-leads-table {
     display: flex;
     padding: 0px;
     position: relative;
     margin: 0px!important;
 }

 .with-leads-table .ls-charts {
     height: calc(100vh - 112px);
 }

 .with-leads-table.with-leads-table-active .ls-charts {
     height: 100%;
     flex-grow: 1;
     flex-basis: 35%;
     padding-bottom: 20px;
 }

 .with-leads-table.with-leads-table-active .ls-leads-table {
     height: 100%;
     flex-grow: 1;
     flex-basis: 65%;
 }

 .ls-charts-wrapper {
     flex: 1;
     overflow-y: auto;
 }

 .ls-charts h1 {
     margin-left: 10px;
     margin-right: 10px;
 }

 .ls-charts > .pivots-wrapper {
     display: flex;
     flex-wrap: wrap;
     gap: 10px;
     margin-top: 0px;
     margin-left: 30px;
     margin-right: 30px;
     padding: 10px;
     background-color: #eaf1f6;
     border: #e3e3e3;
     border-radius: 4px;
     box-shadow: 2px 2px 10px #d9d9d9;

     .pivot {
         font-size: 1.05em;
         color: #fff;
         background: #91acc3;

         a {
             color: #fff;
         }

         select {
             color: #333;
         }
     }
 }

 #dashboard-canvas h1 {
     padding-bottom: .8em;
 }

 #dashboard-canvas h1 form {
     display: inline-block;
     width: 100%;
     height: 20px;
 }

 #dashboard-canvas h1 input[type="text"] {
     background-color: #efefef;
     display: inline-block;
     width: 90%;
 }

 .vue-grid-placeholder {
     background-color: #333!important;
     border-radius: 4px 4px 4px 4px;
 }
</style>



// WEBPACK FOOTER //
// Dashboard.vue?5b2655d4